import { useRouter, type NavigateOptions } from '@tanstack/react-router'
import { useCallback, type MouseEvent } from 'react'

/**
 * Anything in a row that keeps its own click behaviour. A click that lands on
 * or inside one of these never navigates the row — the first cell's real
 * `<a>` included, so a plain click on it navigates once (as a link), not
 * twice. `data-row-click-ignore` is the escape hatch for a control that is
 * none of the above.
 */
const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="menuitem"], [data-row-click-ignore]'

/**
 * The click reached the row through React's tree, not the DOM: a row menu's items render in a
 * popover portalled to `<body>`, and their clicks still bubble to the row's handler. Choosing
 * "Set an alert" must not also navigate the row away under the menu.
 */
const outsideRow = (event: MouseEvent<HTMLElement>): boolean =>
  !(event.target instanceof Node) || !event.currentTarget.contains(event.target)

/** The click started on something with its own behaviour, inside this row. */
const hitsInteractive = (event: MouseEvent<HTMLElement>): boolean => {
  const target = event.target
  if (!(target instanceof Element)) return false
  const hit = target.closest(INTERACTIVE)
  // `closest` walks past the row; a control that WRAPS the whole table is
  // not "in the row" and must not switch row navigation off.
  return hit !== null && event.currentTarget.contains(hit)
}

/** The user was selecting text in this row (a drag ends in a click), not navigating. */
const selectingInside = (row: HTMLElement): boolean => {
  const selection = typeof window === 'undefined' ? null : window.getSelection()
  if (selection === null || selection.isCollapsed || selection.toString().trim() === '') {
    return false
  }
  return (
    (selection.anchorNode !== null && row.contains(selection.anchorNode)) ||
    (selection.focusNode !== null && row.contains(selection.focusNode))
  )
}

export interface RowNavigationProps {
  onClick?: (event: MouseEvent<HTMLTableRowElement>) => void
  onAuxClick?: (event: MouseEvent<HTMLTableRowElement>) => void
}

/**
 * Makes a whole table row navigate, without a stretched-link overlay.
 *
 * The overlay (`position: relative` on the `<tr>`, `after:absolute
 * after:inset-0` on the row's `<a>`) only works where a `<tr>` is a
 * containing block. WebKit does not make it one, so every row's overlay
 * stretched to the nearest positioned ancestor and the LAST row's link sat
 * over the whole card — and whatever else that ancestor contained — sending
 * every click there to one stock.
 *
 * So the row's one real `<a>` stays in its first cell (keyboard, screen
 * readers, middle-click, "open in new tab" all work on it as a link), and the
 * rest of the row gets a click handler that routes to the same place:
 *
 *  - a click on or inside an interactive element is left alone (see
 *    `INTERACTIVE`), and so is one the element already `preventDefault`ed;
 *  - a click from a portal (a row menu's items) is left alone (`outsideRow`);
 *  - a click that ends a text selection inside the row is left alone;
 *  - ctrl/meta + click, and a middle click, open the row's href in a new tab
 *    — what the same gesture on a link does;
 *  - anything else navigates in place through the router.
 *
 * The `<tr>` gets no role and no tabIndex: the link is the keyboard target,
 * and a focusable row around a focusable link is a nested-interactive
 * violation. The caller adds `cursor-pointer` and the hover background.
 *
 * Returns a per-row factory — one hook call per table, not per row. Pass
 * `undefined` for a row that should not navigate and it returns no handlers.
 */
export function useRowNavigation(): (options: NavigateOptions | undefined) => RowNavigationProps {
  const router = useRouter()

  return useCallback(
    (options: NavigateOptions | undefined): RowNavigationProps => {
      if (options === undefined) return {}

      const openInNewTab = () => {
        window.open(router.buildLocation(options).href, '_blank', 'noopener')
      }

      return {
        onClick: (event) => {
          if (event.defaultPrevented || event.button !== 0 || outsideRow(event)) return
          if (hitsInteractive(event) || selectingInside(event.currentTarget)) return
          if (event.ctrlKey || event.metaKey) {
            openInNewTab()
            return
          }
          void router.navigate(options)
        },
        onAuxClick: (event) => {
          if (event.defaultPrevented || event.button !== 1 || outsideRow(event)) return
          if (hitsInteractive(event)) return
          event.preventDefault()
          openInNewTab()
        },
      }
    },
    [router],
  )
}
